import * as React from 'react';

import { Xmark } from 'iconoir-react';
import styled from 'styled-components';

import { CircledTick, Exclamation, Info } from '../../packages/icons';

export type Severity = 'error' | 'warning' | 'success' | 'info' | 'none';

export type Variant = 'outlined' | 'shadowed' | 'simple';

export interface AlertProps {
    variant: Variant;
    children?: React.ReactNode;
    dataId?: string;
    severity?: Severity;
    fadeIn?: boolean;
    width?: string;
    maxWidth?: string;
    /** Can be used to trigger the exit animation manually, by changing from true to false. */
    isVisible?: boolean;
    /** Function called when the dismiss button is clicked. If not provided, the dismiss button will not be rendered */
    onDismiss?: () => void;
}

const colors = new Map<Severity, string>([
    ['error', '#EE7168'],
    ['warning', '#FFAC5B'],
    ['success', '#4DC082'],
    ['info', '#00B1FF'],
]);

interface AlertContainerProps {
    accentColor?: string;
    variant: Variant;
    isHidden: boolean;
    isDismissable: boolean;
    width?: string;
    maxWidth?: string;
}

const AlertContainer = styled.div<AlertContainerProps>`
    position: relative;
    display: flex;
    column-gap: 10px;
    box-sizing: border-box;
    height: fit-content;
    min-width: 100px;
    width: ${(props) => props.width ?? 'fit-content'};
    max-width: ${(props) => (props.maxWidth ? `min(${props.maxWidth}, 100%)` : '100%')};
    font-weight: 600;
    font-size: 13px;
    line-height: 1.4;
    /* TODO use TEXT_DEFAULT color varaible from our modernscripts themes.ts after we move this component out from spotlight ui */
    color: #031c44;
    padding-top: 11px;
    padding-bottom: 11px;
    padding-left: ${(props) => (props.accentColor ? '14px' : '12px')};
    padding-right: ${(props) => (props.isDismissable ? '10px' : '12px')};
    background-color: ${(props) => (props.variant === 'simple' ? 'transparent' : '#fff')};
    opacity: ${(props) => (props.isHidden ? 0 : 1)};
    transition: opacity 300ms ease-in-out;
    border-radius: 5px;
    border: ${(props) => (props.variant === 'outlined' ? `1px solid ${props.accentColor ?? '#CDD3DE'}` : 'none')};

    ${(props) =>
        props.variant === 'shadowed' &&
        `
        box-shadow: 0px 2px 25px rgba(0, 0, 0, 0.08), 0px 1.4px 10.4px rgba(0, 0, 0, 0.055),
        0px 0.9px 5.6px rgba(0, 0, 0, 0.042), 0px 0.6px 3.1px rgba(0, 0, 0, 0.033), 0px 0.3px 1.7px rgba(0, 0, 0, 0.024),
        0px 0.1px 0.7px rgba(0, 0, 0, 0.014);
    `}

    ${(props) =>
        props.accentColor &&
        `
        &:before {
            content: '';
            width: 4px;
            background: ${props.accentColor};
            position: absolute;
            left: 0px;
            top: 0;
            bottom: 0px;
            border-radius: 5px 0 0 5px;
        }
    `}

    ${(props) =>
        props.variant === 'simple' &&
        ` 
        font-size: 14px;
        box-shadow: 'unset';

        &:before {
            content: unset;
            }
        `}
`;

const IconContainer = styled.div`
    flex-shrink: 0;
    margin-top: 1px;
`;

const ContentContainer = styled.div`
    flex-grow: 1;
    overflow-wrap: anywhere;
    display: flex;
    align-items: center;
    min-height: 20px;
`;

const DismissButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 3px;
    height: fit-content;
    border: none;
    border-radius: 50%;
    background-color: transparent;
    cursor: pointer;

    &:hover {
        background-color: #f3f4f7;
    }
`;

export const Alert: React.FC<React.PropsWithChildren<AlertProps>> = ({
    variant,
    children,
    dataId = 'alert',
    severity = 'none',
    fadeIn,
    width,
    maxWidth,
    isVisible = true,
    onDismiss,
    ...props
}) => {
    const [isFirstRender, setIsFirstRender] = React.useState(true);
    const [isClosing, setIsClosing] = React.useState(false);

    const accentColor = colors.get(severity);

    const handleDismiss = () => {
        setIsClosing(true);
        onDismiss && onDismiss();
    };

    React.useEffect(() => {
        setIsFirstRender(false);
    }, []);

    React.useEffect(() => {
        if (!isVisible) {
            setIsClosing(true);
        }

        if (isVisible) {
            setIsClosing(false);
        }
    }, [isVisible]);

    return (
        <AlertContainer
            accentColor={accentColor}
            variant={variant}
            width={width}
            maxWidth={maxWidth}
            isHidden={(fadeIn && isFirstRender) || isClosing}
            isDismissable={!!onDismiss}
            data-test-id={dataId}
            {...props}
        >
            {severity !== 'none' && (
                <IconContainer>
                    {(severity === 'error' || severity === 'warning') && (
                        <Exclamation fill={accentColor} height={'18px'} width={'18px'} />
                    )}
                    {severity === 'success' && <CircledTick fill={accentColor} height={'18px'} width={'18px'} />}
                    {severity === 'info' && <Info fill={accentColor} height={'18px'} width={'18px'} />}
                </IconContainer>
            )}

            <ContentContainer>{children}</ContentContainer>
            {onDismiss && (
                <DismissButton aria-label={'Dismiss'} onClick={handleDismiss} data-test-id={`${dataId}__btn-dismiss`}>
                    <Xmark width={'14px'} height={'14px'} strokeWidth={1.25} />
                </DismissButton>
            )}
        </AlertContainer>
    );
};

Alert.displayName = 'Alert';
